import { httpTrafficLights } from 'shared/services/http/httpClient';

export interface ContingencyReason {
  id: number;
  key: string;
  label: string;
  is_active: boolean;
}

export interface ContingencyType {
  id: number;
  key: string;
  label: string;
  is_active: boolean;
}

export interface CreateContingencyPayload {
  contingency_reason_id: number;
  contingency_type_id: number;
  applied_at: string;
  estimated_duration_minutes: number;
}

export interface UpdateContingencyPayload {
  contingency_reason_id: number;
  contingency_type_id: number;
  applied_at: string;
  estimated_duration_minutes: number;
  observation?: string | null;
}

export interface Contingency {
  id: number;
  applied_at: string;
  applied_by_user_id: number | null;
  applied_by_user_name: string | null;
  finished_at: string | null;
  duration_minutes: number | null;
  observation: string | null;
  polygon_zone_id: number | null;
  contingency_reason_id: number;
  contingency_type_id: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface ContingencyListResponse {
  total: number;
  page: string | number;
  page_size: string | number;
  rows: Contingency[];
}

export interface ContingencyListParams {
  page?: number;
  page_size?: number;
  sort?: string;
  sort_dir?: 'ASC' | 'DESC';
  polygon_zone_id?: number;
  contingency_reason_id?: number;
  contingency_type_id?: number;
  applied_by_user_id?: number;
  applied_at?: string;
  finished_at?: string;
  observation?: string;
}

export interface ActiveContingency {
  id: number;
  applied_at: string;
  finished_at: string;
  applied_by_user_id: number;
  observation: string;
  polygon_zone_id: number;
  contingency_reason_id: number;
  contingency_type_id: number;
  polygon_zone: { id: number; name: string };
  contingency_reason: { id: number; key: string; label: string };
  contingency_type: { id: number; key: string; label: string };
}

const cleanParams = <T extends Record<string, unknown>>(params: T) => {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  );
};

export const contingencyService = () => {
  const getContingencyReasons = async (): Promise<ContingencyReason[]> => {
    const response = await httpTrafficLights.get('contingency-reasons', {
      params: { page: 1, page_size: 100, sort: 'updated_at', sort_dir: 'DESC' }
    });
    return response.rows;
  };

  const getContingencyTypes = async (): Promise<ContingencyType[]> => {
    const response = await httpTrafficLights.get('contingency-types', {
      params: { page: 1, page_size: 100, sort: 'updated_at', sort_dir: 'DESC' }
    });
    return response.rows;
  };

  const createContingency = async (
    payload: CreateContingencyPayload
  ): Promise<void> => {
    await httpTrafficLights.post('contingencies', payload);
  };

  const updateContingency = async (
    id: number,
    payload: UpdateContingencyPayload
  ): Promise<void> => {
    await httpTrafficLights.put(`contingencies/${id}`, payload);
  };

  const deleteContingency = async (id: number): Promise<void> => {
    await httpTrafficLights.delete(`contingencies/${id}`);
  };

  const getContingencies = async (
    params: ContingencyListParams
  ): Promise<ContingencyListResponse> => {
    return httpTrafficLights.get('contingencies', {
      params: cleanParams({
        page: 1,
        page_size: 10,
        sort: 'updated_at',
        sort_dir: 'DESC',
        ...params
      })
    });
  };

  const getContingencyById = async (id: number): Promise<Contingency> => {
    return httpTrafficLights.get(`contingencies/${id}`);
  };

  const getActiveContingencies = async (): Promise<ActiveContingency[]> => {
    return httpTrafficLights.get('contingencies/active');
  };

  return {
    getContingencyReasons,
    getContingencyTypes,
    createContingency,
    updateContingency,
    deleteContingency,
    getContingencies,
    getContingencyById,
    getActiveContingencies
  };
};
